/**
 * Image Placeholder Service
 * 
 * Handles image placeholders in product descriptions (body_html).
 * Placeholders are resolved to actual <img> tags after images are uploaded
 * and their URLs are known.
 * 
 * Placeholder Format:
 *   {{IMAGE_1}}    → First product image (specific)
 *   {{IMAGE_2}}    → Second product image (specific)
 *   {{IMAGE_N}}    → N-th product image (specific)
 *   {{IMAGE_NEXT}} → Next unused image (auto-picks sequentially, skips image 1 when possible)
 */

// Regex to match image placeholders: {{IMAGE_1}}, {{IMAGE_2}}, ..., {{IMAGE_NEXT}}
const IMAGE_PLACEHOLDER_REGEX = /\{\{IMAGE_(\d+|NEXT)\}\}/gi;

/**
 * Count the number of available images on a product.
 * Handles different product image formats (edges, array, etc.)
 * 
 * @param {object} product - The product object
 * @returns {number} Number of images
 */
export function countProductImages(product) {
  if (!product) return 0;

  if (product.images?.edges) {
    return product.images.edges.length;
  }
  if (Array.isArray(product.images)) {
    return product.images.length;
  }
  return 0;
}

/**
 * Get all image URLs from a product in order.
 * 
 * @param {object} product - The product object
 * @returns {string[]} Array of image URLs
 */
export function getProductImageUrls(product) {
  if (!product) return [];

  let images = [];

  if (product.images?.edges) {
    images = product.images.edges.map(edge => edge.node);
  } else if (Array.isArray(product.images)) {
    images = product.images;
  }

  return images.map(img => img.url || img.originalSrc || img.src).filter(Boolean);
}

/**
 * Get thumbnail-sized image info for UI preview.
 * Returns objects with { index, url, alt } for each image.
 * 
 * @param {object} product - The product object
 * @returns {{ index: number, url: string, alt: string }[]}
 */
export function getProductImagePreviews(product) {
  if (!product) return [];

  let images = [];

  if (product.images?.edges) {
    images = product.images.edges.map(edge => edge.node);
  } else if (Array.isArray(product.images)) {
    images = product.images;
  }

  return images.map((img, i) => ({
    index: i + 1, // 1-based for user display
    url: img.url || img.originalSrc || img.src || '',
    alt: img.altText || img.alt || `Image ${i + 1}`
  })).filter(item => item.url);
}

/**
 * Count how many image placeholders are in an HTML string.
 * 
 * @param {string} html - The HTML content
 * @returns {{ total: number, indices: number[], hasNext: boolean }}
 */
export function countImagePlaceholders(html) {
  if (!html || typeof html !== 'string') {
    return { total: 0, indices: [], hasNext: false };
  }

  const indices = [];
  let hasNext = false;
  let nextCount = 0;
  let match;
  const regex = new RegExp(IMAGE_PLACEHOLDER_REGEX.source, 'gi');

  while ((match = regex.exec(html)) !== null) {
    const value = match[1].toUpperCase();
    if (value === 'NEXT') {
      hasNext = true;
      nextCount++;
    } else {
      const num = parseInt(value, 10);
      if (!isNaN(num) && !indices.includes(num)) {
        indices.push(num);
      }
    }
  }

  indices.sort((a, b) => a - b);

  return {
    total: indices.length + nextCount,
    indices,
    hasNext,
    nextCount
  };
}

/**
 * Check if HTML content contains any image placeholders.
 * 
 * @param {string} html - The HTML content
 * @returns {boolean}
 */
export function hasImagePlaceholders(html) {
  if (!html || typeof html !== 'string') return false;
  return IMAGE_PLACEHOLDER_REGEX.test(html);
}

/**
 * Generate the prompt hint that tells the AI how many images are available
 * and how to use image placeholders.
 * 
 * @param {number} imageCount - Number of available product images
 * @param {string} mode - Placeholder mode ('manual' or 'smart')
 * @param {object[]} [imageAnalyses] - Smart mode: analysis results per image
 * @returns {string} Prompt addition text
 */
export function generatePromptHint(imageCount, mode = 'manual', imageAnalyses = null) {
  if (imageCount === 0) {
    return '\n\n[Note: This product has no images. Do not use any image placeholders.]';
  }

  // Smart mode: use the detailed analysis-based prompt hint
  if (mode === 'smart' && imageAnalyses && imageAnalyses.length > 0) {
    return generateSmartPromptHint(imageAnalyses);
  }

  // Manual mode: tell the AI about IMAGE_NEXT
  return `\n\n[Image Information: This product has ${imageCount} image${imageCount > 1 ? 's' : ''}. ` +
    `You MUST embed images directly into your generated HTML description using placeholders:\n` +
    `- Inject {{IMAGE_NEXT}} directly between your <p> or <ul> tags to automatically insert the next available product image.\n` +
    (imageCount > 1 
      ? `- Alternatively, use {{IMAGE_2}} to {{IMAGE_${imageCount}}} to insert a specific image by number.\n`
      : `- Alternatively, use {{IMAGE_1}} to insert the product image.\n`) +
    `\nPlace these placeholders naturally in the HTML where an image would enhance the content. ` +
    `Do NOT use {{IMAGE_1}} if there are multiple images, as it is typically the main product photo already shown at the top of the product page.]`;
}

/**
 * Generate a detailed prompt hint from image analysis results (Smart Mode).
 * The AI knows exactly what each image shows and can place them intelligently.
 * 
 * @param {object[]} analysisResults - Array of analysis results per image
 * @returns {string} Rich prompt context for AI description generation
 */
export function generateSmartPromptHint(analysisResults) {
  if (!analysisResults || analysisResults.length === 0) {
    return '';
  }

  const imageList = analysisResults.map((analysis, i) => {
    const idx = analysis.imageIndex || (i + 1);
    const cat = analysis.category ? ` [${analysis.category}]` : '';
    const pos = analysis.bestPosition ? ` → recommended position: ${analysis.bestPosition}` : '';
    const desc = analysis.description || 'No description available';
    const alt = analysis.suggestedAltText ? `\n  Alt: "${analysis.suggestedAltText}"` : '';
    return `- Image ${idx}${cat}: "${desc}"${alt}${pos}`;
  }).join('\n');

  // Build a summary of detected visual attributes across all images
  const categories = analysisResults.map(a => a.category).filter(Boolean);
  const uniqueCategories = [...new Set(categories)];
  const variantImages = analysisResults.filter(a => a.category === 'variant');
  
  let variantHint = '';
  if (variantImages.length > 0) {
    variantHint = `\n\nVARIANT IMAGES DETECTED: ${variantImages.length} images show different color/style variants. ` +
      `Use these descriptions to identify available product variants (colors, materials, etc.) and mention them in the product description.`;
  }

  return `\n\n## Available Product Images (analyzed by Vision AI)\n` +
    `The following ${analysisResults.length} images have been analyzed with detailed descriptions of what each image shows ` +
    `(colors, materials, angles, features, variants). Use this information to write a more accurate and detailed product description.\n\n` +
    `${imageList}${variantHint}\n\n` +
    `IMPORTANT: You MUST embed 2-3 appropriate images directly into your generated HTML content using image placeholders.\n` +
    `- Inject placeholders like {{IMAGE_2}} or {{IMAGE_3}} directly between your <p> or <ul> tags where relevant.\n` +
    `- Use {{IMAGE_N}} to insert a specific image by number based on the analysis above.\n` +
    `- Do NOT use {{IMAGE_1}} — it is the main product image already displayed above the description.\n` +
    `- You do NOT need to use all images. Pick the 2-3 that best complement your description.\n` +
    `- USE the image descriptions to enrich your product text with accurate details about colors, materials, features, and available variants.`;
}

/**
 * Resolve image placeholders in HTML content by replacing them with actual <img> tags.
 * 
 * Resolution order:
 * 1. First, resolve all specific {{IMAGE_N}} placeholders
 * 2. Then, resolve {{IMAGE_NEXT}} placeholders sequentially using unused images
 *    - Starts from Image 2 (skips the featured/hero image) when >1 image available
 *    - If all non-featured images are used, cycles back to Image 1
 * 
 * @param {string} html - HTML content containing image placeholders
 * @param {string[]} imageUrls - Array of uploaded image URLs (0-indexed)
 * @param {object} [options] - Options for image rendering
 * @returns {string} HTML with placeholders replaced by img tags
 */
export function resolveImagePlaceholders(html, imageUrls, options = {}) {
  if (!html || typeof html !== 'string') return html;
  if (!imageUrls || imageUrls.length === 0) {
    // Remove all placeholders if no images available
    return html.replace(IMAGE_PLACEHOLDER_REGEX, '');
  }

  const {
    cssClass = 'product-description-image',
    altPrefix = '',
    analysisResults = null
  } = options;

  // ── PRE-PROCESSING: Normalize AI-generated <img> tags back to raw placeholders ──
  // The AI sometimes generates full <img> tags with {{IMAGE_N}} in src/data-image-placeholder
  // attributes instead of just emitting the raw {{IMAGE_N}} placeholder text.
  // If we resolve without fixing this first, the regex replaces {{IMAGE_N}} INSIDE attributes,
  // producing nested/malformed HTML like: data-image-placeholder="<img src="..." ...>"
  // Fix: Replace any <img> tag containing {{IMAGE_N}} or {{IMAGE_NEXT}} in its attributes
  // with just the extracted placeholder(s).
  let preprocessed = html.replace(/<img\s[^>]*\{\{IMAGE_([\dNEXT]+)\}\}[^>]*>/gi, (fullMatch, placeholderNum) => {
    const placeholder = `{{IMAGE_${placeholderNum.toUpperCase()}}}`;
    console.log(`[ImagePlaceholder] Pre-processing: Stripped AI-generated <img> tag containing ${placeholder} back to raw placeholder`);
    return placeholder;
  });

  // Track which images have been used by specific {{IMAGE_N}} placeholders
  const usedIndices = new Set();

  // --- Pass 1: Resolve specific {{IMAGE_N}} placeholders ---
  let resolved = preprocessed.replace(/\{\{IMAGE_(\d+)\}\}/gi, (match, value) => {
    const index = parseInt(value, 10);
    if (isNaN(index) || index < 1 || index > imageUrls.length) {
      console.warn(`Image placeholder ${match} is out of range (have ${imageUrls.length} images). Removing.`);
      return '';
    }

    usedIndices.add(index);
    const url = imageUrls[index - 1];
    const alt = getAltText(index, altPrefix, analysisResults);
    return buildImgTag(url, alt, cssClass, `{{IMAGE_${index}}}`);
  });

  // --- Pass 2: Resolve {{IMAGE_NEXT}} placeholders ---
  // Build list of available images for NEXT, starting from image 2 (skip featured)
  const nextQueue = [];
  if (imageUrls.length === 1) {
    // Only 1 image: use it if not already used
    if (!usedIndices.has(1)) nextQueue.push(1);
  } else {
    // Multiple images: start from image 2 (skip hero/featured image 1)
    for (let i = 2; i <= imageUrls.length; i++) {
      if (!usedIndices.has(i)) nextQueue.push(i);
    }
    // If all secondary images used, fall back to image 1
    if (nextQueue.length === 0 && !usedIndices.has(1)) {
      nextQueue.push(1);
    }
  }

  let nextIndex = 0;
  resolved = resolved.replace(/\{\{IMAGE_NEXT\}\}/gi, (match) => {
    if (nextIndex >= nextQueue.length) {
      // No more unused images — remove the placeholder
      console.warn('{{IMAGE_NEXT}} has no more unused images. Removing.');
      return '';
    }
    const imgNum = nextQueue[nextIndex];
    nextIndex++;
    usedIndices.add(imgNum);
    const url = imageUrls[imgNum - 1];
    const alt = getAltText(imgNum, altPrefix, analysisResults);
    return buildImgTag(url, alt, cssClass, `{{IMAGE_${imgNum}}}`);
  });

  // ── POST-PROCESSING: Sanitize any broken data-image-placeholder attributes ──
  // Catch-all for any remaining malformed placeholders (e.g., from HTML-encoded AI output).
  // Valid format: data-image-placeholder="{{IMAGE_N}}" — anything else is broken.
  resolved = resolved.replace(/<img\s[^>]*data-image-placeholder="(?!\{\{IMAGE_\d+\}\})[^"]*"[^>]*>/gi, (brokenTag) => {
    // Try to extract position from the src URL (e.g., "-8.jpg" → position 8)
    const srcMatch = brokenTag.match(/src="([^"]*)"/);
    if (!srcMatch) return brokenTag; // Can't fix without src

    // Remove all duplicate/broken attributes and rebuild a clean tag
    const src = srcMatch[1];
    const altMatch = brokenTag.match(/alt="([^"]*)"/);
    const alt = altMatch ? altMatch[1] : '';
    const classMatch = brokenTag.match(/class="([^"]*)"/);
    const cls = classMatch ? classMatch[1] : cssClass;

    // Try to find which IMAGE_N this corresponds to by matching the src URL
    let placeholderValue = '';
    for (let i = 0; i < imageUrls.length; i++) {
      if (src.includes(imageUrls[i].split('?')[0].substring(imageUrls[i].split('?')[0].lastIndexOf('/') + 1))) {
        placeholderValue = `{{IMAGE_${i + 1}}}`;
        break;
      }
    }

    if (!placeholderValue) {
      // Fallback: check if src filename matches any CDN URL's filename
      const srcFilename = src.split('?')[0].substring(src.split('?')[0].lastIndexOf('/') + 1);
      for (let i = 0; i < imageUrls.length; i++) {
        const urlFilename = imageUrls[i].split('?')[0].substring(imageUrls[i].split('?')[0].lastIndexOf('/') + 1);
        if (srcFilename === urlFilename) {
          placeholderValue = `{{IMAGE_${i + 1}}}`;
          break;
        }
      }
    }

    console.log(`[ImagePlaceholder] Post-processing: Fixed broken data-image-placeholder → ${placeholderValue || 'removed'}`);
    return buildImgTag(src, alt, cls, placeholderValue);
  });

  return resolved;
}

/**
 * Get alt text for an image, using analysis results if available.
 */
function getAltText(imageIndex, altPrefix, analysisResults) {
  if (analysisResults && analysisResults[imageIndex - 1]?.suggestedAltText) {
    return analysisResults[imageIndex - 1].suggestedAltText;
  }
  return altPrefix ? `${altPrefix} - Image ${imageIndex}` : `Product Image ${imageIndex}`;
}

/**
 * Build an HTML <img> tag.
 */
function buildImgTag(url, alt, cssClass, placeholderValue = '') {
  const dataPlaceholder = placeholderValue ? ` data-image-placeholder="${placeholderValue}"` : '';
  return `<img src="${url}" alt="${alt}" class="${cssClass}"${dataPlaceholder} style="max-width: 100%; height: auto;" loading="lazy">`;
}

/**
 * Reverts resolved image HTML back to their original {{IMAGE_N}} placeholders.
 * Useful when copying/duplicating products so that new images can be assigned correctly.
 * 
 * @param {string} html - HTML containing resolved <img> tags with data-image-placeholder attributes
 * @returns {string} HTML with reverted placeholders
 */
export function revertResolvedImagesToPlaceholders(html) {
  if (!html || typeof html !== 'string') return html;
  
  // Find <img ... data-image-placeholder="{{IMAGE_X}}" ... /> and extract the placeholder
  return html.replace(/<img[^>]*data-image-placeholder="(\{\{IMAGE_\d+\}\})"[^>]*>/gi, (match, placeholder) => {
    return placeholder;
  });
}

/**
 * Validate that all image placeholders in HTML can be resolved.
 * Returns warnings for any placeholders that reference images beyond the available count.
 * 
 * @param {string} html - The HTML content
 * @param {number} availableImageCount - Number of available images
 * @returns {{ valid: boolean, warnings: string[] }}
 */
export function validateImagePlaceholders(html, availableImageCount) {
  const { indices, hasNext, nextCount } = countImagePlaceholders(html);
  const warnings = [];

  for (const index of indices) {
    if (index > availableImageCount) {
      warnings.push(`{{IMAGE_${index}}} references Image ${index}, but the product only has ${availableImageCount} image${availableImageCount > 1 ? 's' : ''}.`);
    }
  }

  if (hasNext && availableImageCount === 0) {
    warnings.push('{{IMAGE_NEXT}} cannot be resolved — the product has no images.');
  }

  // Check if there are enough images for all NEXT placeholders
  const specificCount = indices.filter(i => i <= availableImageCount).length;
  const remainingImages = availableImageCount - specificCount;
  if (nextCount > remainingImages) {
    warnings.push(`${nextCount} {{IMAGE_NEXT}} placeholders but only ${remainingImages} unused image${remainingImages !== 1 ? 's' : ''} remaining.`);
  }

  return {
    valid: warnings.length === 0,
    warnings
  };
}

export default {
  countProductImages,
  getProductImageUrls,
  getProductImagePreviews,
  countImagePlaceholders,
  hasImagePlaceholders,
  generatePromptHint,
  generateSmartPromptHint,
  resolveImagePlaceholders,
  revertResolvedImagesToPlaceholders,
  validateImagePlaceholders
};
